/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { Fragment, useCallback, useLayoutEffect, useEffect, useRef, useState, type KeyboardEvent } from 'react';

import { css, jsx, keyframes } from '@compiled/react';
import { FormattedMessage, useIntl, type MessageDescriptor } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Form from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import { IconTile } from '@atlaskit/icon';
import ArrowUpIcon from '@atlaskit/icon/core/arrow-up';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import Image from '@atlaskit/image';
import Lozenge from '@atlaskit/lozenge';
import Modal, { ModalBody, ModalTransition } from '@atlaskit/modal-dialog';
import { useResizingHeight } from '@atlaskit/motion';
import Popup, { type TriggerProps } from '@atlaskit/popup';
import { Box, Flex, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import TextArea from '@atlaskit/textarea';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import remixIllustrationSvg from './assets/Remix-Illustration.svg';
import { SUB_TYPE_ICON_MAP } from './constants';
import messages from './messages';
import type {
	RemixOption,
	RemixOptionKind,
	RemixOptionSelectionParameters,
	RemixSubType,
} from './types';
import { REMIX_OPTIONS } from './utils';

export type {
	KnownSubType,
	RemixOptionKind,
	RemixOptionSelectionParameters,
	RemixOption,
} from './types';
export { getTypeDisplayName, getSubTypeDisplayName, PAGE_REMIX_OPTIONS } from './utils';
export { default as remixOverlayMessages } from './messages';

// ── RemixOverlayHeader styles ──────────────────────────────────────────────
const headerOuterStyle = css({
	position: 'relative',
	width: '100%',
	paddingBlockStart: token('space.300'), // 24px top spacing
	paddingBlockEnd: token('space.300'), // 24px bottom spacing before accordion list
	transition: 'padding 200ms ease-in-out',
});

const headerOuterMinimizedStyle = css({
	paddingBlockStart: token('space.200'), // 16px top spacing in minimized state
	paddingBlockEnd: token('space.200'), // 16px bottom spacing in minimized state
});

const labsPositionStyle = css({
	position: 'absolute',
	top: token('space.200'), // 16px from top edge
	right: token('space.200'), // 16px from right edge
	cursor: 'default',
	zIndex: 1,
});

const headerIconWrapperStyle = css({
	position: 'absolute',
	left: '50%',
	top: token('space.300'), // 24px — aligned with container top padding
	transform: 'translateX(-50%)',
	width: '64px', // full-size icon in non-expanded state
	height: '64px',
	transition: 'left 200ms ease-in-out, top 200ms ease-in-out, width 200ms ease-in-out, height 200ms ease-in-out, transform 200ms ease-in-out, opacity 100ms ease-out',
});

const headerIconWrapperMinimizedStyle = css({
	left: token('space.200'), // 16px from left
	top: token('space.200'), // 16px from top in minimized state
	transform: 'translateX(0)',
	width: '48px', // reduced icon size in minimized state
	height: '48px',
});

// 92px = space.300 (24px top padding) + 64px icon + 4px gap
const headerHeadingStyle = css({
	position: 'absolute',
	left: '50%',
	top: '92px',
	transform: 'translateX(-50%)',
	transition: 'left 200ms ease-in-out, top 200ms ease-in-out, transform 200ms ease-in-out',
	whiteSpace: 'nowrap',
});

// left: 16px (space.200) + 48px icon + 8px gap = 72px
// top: vertically centered with 48px icon — icon center is at 16px + 24px = 40px,
// heading line-height ~24px, so top = 40px - 12px = 28px
const headerHeadingMinimizedStyle = css({
	left: `calc(${token('space.200')} + 56px)`,
	top: '28px',
	transform: 'translateX(0)',
});

// 120px = 92px heading top + ~28px heading height
const headerSubtitleStyle = css({
	position: 'absolute',
	left: '50%',
	top: '120px',
	transform: 'translateX(-50%)',
	transition:
		'left 200ms ease-in-out, top 200ms ease-in-out, transform 200ms ease-in-out, opacity 60ms ease-in 170ms',
	whiteSpace: 'nowrap',
});

// Hide subtitle in minimized state — fast fade out, delayed fade in handled by base style
const headerSubtitleMinimizedStyle = css({
	opacity: 0,
	pointerEvents: 'none',
	transition:
		'left 200ms ease-in-out, top 200ms ease-in-out, transform 200ms ease-in-out, opacity 30ms ease-out',
});

const subtitleStyle = css({
	color: token('color.text.subtlest'),
});

// 113px = 64px icon + 4px gap + ~28px heading + ~4px gap + ~13px subtitle
const headerSpacerStyle = css({
	height: '113px',
	transition: 'height 200ms ease-in-out',
});

// 48px = minimized icon height (no subtitle in minimized state)
const headerSpacerMinimizedStyle = css({
	height: '48px',
});

// ── RemixSubTypeGrid styles ────────────────────────────────────────────────
const gridStyles = cssMap({
	grid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(3, 1fr)',
		gap: token('space.100'),
		width: '100%',
	},
	tile: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: '60px',
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.large'),
		backgroundColor: token('elevation.surface.overlay'),
		cursor: 'pointer',
		overflow: 'hidden',
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
			borderColor: 'transparent',
		},
	},
});

// ── RemixAccordionOption styles ────────────────────────────────────────────
const accordionStyles = cssMap({
	accordionWrapper: {
		width: '100%',
		borderRadius: token('radius.large'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
	},
	accordionHeader: {
		width: '100%',
		height: '48px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		backgroundColor: token('color.background.neutral.subtle'),
		borderWidth: '0',
		borderRadius: token('radius.large'),
		paddingBlock: token('space.100'),
		paddingInlineStart: token('space.150'),
		paddingInlineEnd: token('space.150'),
		cursor: 'pointer',
		'&:focus-visible': {
			outline: 'none',
		},
	},
	expandedContent: {
		paddingInline: token('space.150'),
		paddingBlockEnd: token('space.0'),
	},
	pickForMeWrapper: {
		paddingBlockStart: token('space.075'),
		paddingBlockEnd: token('space.075'),
		paddingInline: token('space.075'),
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
		marginBlockStart: token('space.150'),
	},
});

const accordionCollapsedStyle = css({
	backgroundColor: token('elevation.surface.overlay'),
	transition: 'background-color 200ms ease-out, box-shadow 200ms ease-out',
	borderRadius: token('radius.large'),
	'&:hover': {
		backgroundColor: token('elevation.surface.hovered'),
		boxShadow: token('elevation.shadow.overlay'),
	},
});

const accordionExpandedStyle = css({
	backgroundColor: token('elevation.surface.overlay'),
	borderRadius: token('radius.large'),
});

const revealContentStyle = css({
	overflow: 'hidden',
});

const pickForMeButtonStyle = css({
	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		fontWeight: token('font.weight.regular'),
		width: '100%',
		borderRadius: token('radius.small'),
	},
});

// ── RemixOverlay styles ────────────────────────────────────────────────────
const modalContentWrapperStyle = css({
	display: 'flex',
	flexDirection: 'column',
	flex: '1 1 auto',
	overflow: 'hidden',
	backgroundColor: token('elevation.surface.sunken'),
	borderRadius: 'inherit',
});

// ── RemixPopup styles ──────────────────────────────────────────────────────
const subtleSlideIn = keyframes({
	from: { opacity: 0, transform: 'translateX(-8px)' },
	to: { opacity: 1, transform: 'translateX(0)' },
});

const popupEnterAnimationStyle = css({
	animationName: subtleSlideIn,
	animationDuration: '200ms',
	animationTimingFunction: 'cubic-bezier(0.2, 0, 0, 1)',
	animationFillMode: 'backwards',
	'@media (prefers-reduced-motion: reduce)': {
		animation: 'none',
	},
});

const popupStyles = cssMap({
	contentWrapper: {
		display: 'flex',
		flexDirection: 'column',
		width: '320px',
		overflow: 'hidden',
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.large'),
		// Shadow is provided by ADS Popup's default popupComponent — no need to add one here.
	},
	body: {
		display: 'flex',
		flexDirection: 'column',
		flex: '1 1 auto',
		overflow: 'hidden',
	},
});

const scrollableBodyStyle = css({
	overflowY: 'auto',
	flex: '1 1 auto',
});

const remixStyles = cssMap({
	describeInputBorderWrapper: {
		width: '100%',
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
	},
	describeInputWrapper: {
		width: '100%',
		paddingBlock: token('space.150'),
		paddingInline: token('space.200'),
		backgroundColor: token('elevation.surface.sunken'),
	},
	describePromptRow: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'flex-end',
		gap: token('space.075'),
		width: '100%',
	},
	describeTextAreaWrapper: {
		flex: '1 1 auto',
		minWidth: '0px',
	},
	modalBodyPadding: {
		paddingInline: token('space.200'),
		paddingBlockStart: token('space.0'),
		paddingBlockEnd: token('space.200'),
	},
	remixOptionsStack: {
		width: '100%',
	},
});

// ── RemixOverlayHeader ─────────────────────────────────────────────────────

function RemixOverlayHeader({
	subtitle,
	isMinimized = false,
}: {
	subtitle?: MessageDescriptor;
	isMinimized?: boolean;
}) {
	const { formatMessage } = useIntl();
	const subtitleMessage = subtitle ?? messages.subtitle;

	// Track minimized state changes to trigger the icon fade-out/fade-in animation.
	// When isMinimized changes, quickly fade out at the start, then fade back in
	// near the end of the 200ms position transition.
	const iconRef = useRef<HTMLDivElement>(null);
	const prevMinimizedRef = useRef(isMinimized);

	useEffect(() => {
		if (prevMinimizedRef.current !== isMinimized) {
			prevMinimizedRef.current = isMinimized;
			const el = iconRef.current;
			if (el) {
				// Gently fade out (uses the 100ms ease-out opacity transition from the base style)
				el.style.opacity = '0';

				// Near the end of the 200ms position transition, fade back in
				const timer = setTimeout(() => {
					el.style.opacity = '1';
				}, 130);
				return () => clearTimeout(timer);
			}
		}
	}, [isMinimized]);

	return (
		<div css={[headerOuterStyle, isMinimized && headerOuterMinimizedStyle]}>
			<Tooltip content={formatMessage(messages.labsLozengeTooltip)} position="top">
				{(tooltipProps) => (
					<div css={labsPositionStyle}>
						<Flex {...tooltipProps} justifyContent="end">
							<Lozenge appearance="new">
								<FormattedMessage {...messages.labsLozenge} />
							</Lozenge>
						</Flex>
					</div>
				)}
			</Tooltip>
			<div
				ref={iconRef}
				css={[
					headerIconWrapperStyle,
					isMinimized && headerIconWrapperMinimizedStyle,
				]}
				aria-hidden="true"
			>
				<Image
					src={remixIllustrationSvg}
					alt={formatMessage(messages.remixIconAlt)}
					width="100%"
				/>
			</div>
			<div css={[headerHeadingStyle, isMinimized && headerHeadingMinimizedStyle]}>
				<Heading size={isMinimized ? "medium" : "large"}>
					<FormattedMessage {...messages.heading} />
				</Heading>
			</div>
			<div css={[headerSubtitleStyle, isMinimized && headerSubtitleMinimizedStyle, subtitleStyle]}>
				<Text size="small">
					<FormattedMessage {...subtitleMessage} />
				</Text>
			</div>
			<div css={[headerSpacerStyle, isMinimized && headerSpacerMinimizedStyle]} />
		</div>
	);
}

// ── RemixSubTypeGrid ───────────────────────────────────────────────────────

interface RemixSubTypeGridProps {
	subTypes: RemixSubType[];
	onSubTypeSelect: (subTypeValue: string) => void;
}

function RemixSubTypeGrid({ subTypes, onSubTypeSelect }: RemixSubTypeGridProps) {
	const { formatMessage } = useIntl();

	return (
		<Box xcss={gridStyles.grid}>
			{subTypes.map((subType) => {
				const IconComponent = SUB_TYPE_ICON_MAP[subType.value as keyof typeof SUB_TYPE_ICON_MAP];
				const label = formatMessage(subType.labelMessage);

				if (process.env.NODE_ENV === 'development' && !IconComponent) {
					// eslint-disable-next-line no-console
					console.warn(`[RemixSubTypeGrid] Missing icon for sub-type: "${subType.value}"`);
				}

				return (
					<Tooltip key={subType.value} content={label} position="top">
						{(tooltipProps) => (
							<Pressable
								{...tooltipProps}
								xcss={gridStyles.tile}
								onClick={() => onSubTypeSelect(subType.value)}
								testId={subType.testId}
								aria-label={label}
							>
								{IconComponent ? <IconComponent /> : null}
							</Pressable>
						)}
					</Tooltip>
				);
			})}
		</Box>
	);
}

// ── RemixAccordionOption ───────────────────────────────────────────────────

interface RemixAccordionOptionProps {
	option: RemixOption;
	isExpanded: boolean;
	onToggle: (type: RemixOptionKind) => void;
	onSubTypeSelect: (type: RemixOptionKind, subType: string) => void;
	onPickForMe: (type: RemixOptionKind) => void;
}

const animationConfig = { duration: () => 450 };

const renderOptionIcon = (option: RemixOption, alt: string) => {
	return (
		<IconTile
			icon={option.icon}
			label={alt}
			appearance={option.iconTileAppearance ?? 'gray'}
			size="small"
			shape="square"
		/>
	);
};

function RemixAccordionOption({
	option,
	isExpanded,
	onToggle,
	onSubTypeSelect,
	onPickForMe,
}: RemixAccordionOptionProps) {
	const { formatMessage } = useIntl();
	const smoothResizer = useResizingHeight(animationConfig);

	const handleToggle = useCallback(() => {
		onToggle(option.type);
	}, [onToggle, option.type]);

	const handleSubTypeSelect = useCallback(
		(subTypeValue: string) => {
			onSubTypeSelect(option.type, subTypeValue);
		},
		[onSubTypeSelect, option.type],
	);

	const handlePickForMe = useCallback(() => {
		onPickForMe(option.type);
	}, [onPickForMe, option.type]);

	const ChevronIcon = isExpanded ? ChevronUpIcon : ChevronDownIcon;
	const optionIconAlt = formatMessage(option.iconAltMessage);

	return (
		<div css={[isExpanded ? accordionExpandedStyle : accordionCollapsedStyle]}>
			<Stack xcss={accordionStyles.accordionWrapper}>
				<Pressable
					xcss={accordionStyles.accordionHeader}
					onClick={handleToggle}
					testId={option.testId}
					aria-expanded={isExpanded}
					aria-label={formatMessage(option.labelMessage)}
				>
					<Flex alignItems="center" gap="space.100">
						{renderOptionIcon(option, optionIconAlt)}
						<Text weight="medium">{formatMessage(option.labelMessage)}</Text>
					</Flex>
					<ChevronIcon label="" color={token('color.icon')} />
				</Pressable>

				<div ref={smoothResizer.ref} css={revealContentStyle}>
					{isExpanded && (
						<Fragment>
							<Box xcss={accordionStyles.expandedContent}>
								<RemixSubTypeGrid
									subTypes={option.subTypes}
									onSubTypeSelect={handleSubTypeSelect}
								/>
							</Box>
							<Box xcss={accordionStyles.pickForMeWrapper}>
								<div css={pickForMeButtonStyle}>
									<Button
										appearance="subtle"
										spacing="compact"
										onClick={handlePickForMe}
										testId={`${option.testId}-pick-for-me`}
									>
										{formatMessage(messages.pickForMe)}
									</Button>
								</div>
							</Box>
						</Fragment>
					)}
				</div>
			</Stack>
		</div>
	);
}

interface RemixFlatOptionProps {
	option: RemixOption;
	onSelect: (type: RemixOptionKind) => void;
}

const RemixFlatOption = ({ option, onSelect }: RemixFlatOptionProps) => {
	const { formatMessage } = useIntl();

	const handleClick = useCallback(() => {
		onSelect(option.type);
	}, [onSelect, option.type]);
	const optionIconAlt = formatMessage(option.iconAltMessage);

	return (
		<div css={accordionCollapsedStyle}>
			<Stack xcss={accordionStyles.accordionWrapper}>
				<Pressable
					xcss={accordionStyles.accordionHeader}
					onClick={handleClick}
					testId={option.testId}
					aria-label={formatMessage(option.labelMessage)}
				>
					<Flex alignItems="center" gap="space.100">
						{renderOptionIcon(option, optionIconAlt)}
						<Text weight="medium">{formatMessage(option.labelMessage)}</Text>
					</Flex>
				</Pressable>
			</Stack>
		</div>
	);
};

interface RemixOverlayContentProps {
	onOptionSelect?: (params: RemixOptionSelectionParameters) => void;
	/** Custom set of options to display. Defaults to REMIX_OPTIONS (selection-level). */
	options?: RemixOption[];
	/** Custom subtitle message. Defaults to "View this content in a new form". */
	subtitle?: MessageDescriptor;
}

interface RemixOverlayProps extends RemixOverlayContentProps {
	onClose: () => void;
}

interface RemixPopupProps extends RemixOverlayContentProps {
	isOpen: boolean;
	onClose: () => void;
	trigger: (triggerProps: TriggerProps) => React.ReactNode;
	placement?: 'left-end';
	offset?: [number, number];
	/** When this value changes the popup will reposition itself. */
	repositionKey?: number;
}

/**
 * A centered modal overlay that displays remix options.
 *
 * Options with sub-types render as expandable accordions with sub-type tiles.
 * Options without sub-types render as flat clickable rows that directly fire selection.
 * Selecting a sub-type emits `{ type, subType }`.
 * The "Pick for me" button emits only `{ type }`, letting the backend choose.
 * The describe input emits `{ type: 'custom', userPrompt }`.
 */
/**
 * Shared inner content for both RemixOverlay (modal) and RemixPopup (popup).
 * Contains the remix options list, accordion, and describe input.
 */
function RemixOverlayContent({ onOptionSelect, options, subtitle }: RemixOverlayContentProps) {
	const { formatMessage } = useIntl();
	const [describeValue, setDescribeValue] = useState('');
	const [expandedOption, setExpandedOption] = useState<RemixOptionKind | null>(null);

	const resolvedOptions = options ?? REMIX_OPTIONS;

	const handleDescribeSubmit = useCallback(() => {
		if (describeValue?.trim()) {
			onOptionSelect?.({ type: 'custom', userPrompt: describeValue });
		}
	}, [describeValue, onOptionSelect]);

	const handleDescribeKeyDown = useCallback(
		(e: KeyboardEvent<HTMLTextAreaElement>) => {
			if (e.key === 'Enter' && !e.shiftKey) {
				e.preventDefault();
				handleDescribeSubmit();
			}
		},
		[handleDescribeSubmit],
	);

	const handleToggle = useCallback((type: RemixOptionKind) => {
		setExpandedOption((prev) => (prev === type ? null : type));
	}, []);

	const handleSubTypeSelect = useCallback(
		(type: RemixOptionKind, subType: string) => {
			onOptionSelect?.({ type, subType });
		},
		[onOptionSelect],
	);

	const handlePickForMe = useCallback(
		(type: RemixOptionKind) => {
			onOptionSelect?.({ type });
		},
		[onOptionSelect],
	);

	return (
		<Fragment>
			<RemixOverlayHeader subtitle={subtitle} isMinimized={expandedOption !== null} />
			<div css={scrollableBodyStyle}>
				<Box xcss={remixStyles.modalBodyPadding}>
					<Stack space="space.100" alignInline="stretch" xcss={remixStyles.remixOptionsStack}>
						{resolvedOptions.map((option) =>
							option.subTypes.length > 0 ? (
								<RemixAccordionOption
									key={option.type}
									option={option}
									isExpanded={expandedOption === option.type}
									onToggle={handleToggle}
									onSubTypeSelect={handleSubTypeSelect}
									onPickForMe={handlePickForMe}
								/>
							) : (
								<RemixFlatOption key={option.type} option={option} onSelect={handlePickForMe} />
							),
						)}
					</Stack>
				</Box>
			</div>
			<Box xcss={remixStyles.describeInputBorderWrapper}>
				<Box xcss={remixStyles.describeInputWrapper}>
					<Form onSubmit={handleDescribeSubmit}>
						<Flex xcss={remixStyles.describePromptRow}>
							<Box xcss={remixStyles.describeTextAreaWrapper}>
								<TextArea
									appearance="none"
									placeholder={formatMessage(messages.describePlaceholder)}
									value={describeValue}
									onChange={(e) => setDescribeValue(e.currentTarget.value)}
									onKeyDown={handleDescribeKeyDown}
									isCompact
									minimumRows={1}
									maxHeight="200px"
									resize="smart"
									aria-label={formatMessage(messages.describePlaceholder)}
									testId="remix-overlay-describe-input"
									name="remix-overlay-describe"
								/>
							</Box>
							<IconButton
								type="submit"
								appearance="primary"
								isDisabled={!describeValue?.trim()}
								icon={ArrowUpIcon}
								label={formatMessage(messages.describeSubmitLabel)}
								testId="remix-overlay-describe-submit"
							/>
						</Flex>
					</Form>
				</Box>
			</Box>
		</Fragment>
	);
}

/**
 * RemixOverlay — centered ADS Modal presentation
 */
export function RemixOverlay({ onClose, onOptionSelect, options, subtitle }: RemixOverlayProps) {
	return (
		<ModalTransition>
			<Modal
				onClose={onClose}
				width={320}
				testId="remix-overlay"
				shouldReturnFocus={false}
				autoFocus={false}
			>
				<div css={modalContentWrapperStyle}>
					<ModalBody hasInlinePadding={false}>
						<RemixOverlayContent
							onOptionSelect={onOptionSelect}
							options={options}
							subtitle={subtitle}
						/>
					</ModalBody>
				</div>
			</Modal>
		</ModalTransition>
	);
}

/**
 * Calls the Popup `update` function to reposition the popup whenever `repositionKey` changes.
 */
function PopupRepositionOnKeyChange({
	repositionKey,
	update,
}: {
	repositionKey?: number;
	update: () => void;
}) {
	const prevKey = useRef(repositionKey);
	useLayoutEffect(() => {
		if (prevKey.current !== repositionKey) {
			prevKey.current = repositionKey;
			update();
		}
	}, [repositionKey, update]);
	return null;
}

/**
 * RemixPopup — anchored ADS Popup presentation for contextual entry points (e.g. object-level sidebar).
 * No blanket, anchored to a trigger element.
 */
export function RemixPopup({
	isOpen,
	onClose,
	trigger,
	onOptionSelect,
	options,
	subtitle,
	placement = 'left-end',
	offset = [8, 16],
	repositionKey,
}: RemixPopupProps) {
	return (
		<Popup
			isOpen={isOpen}
			onClose={onClose}
			placement={placement}
			offset={offset}
			label="Remix"
			role="dialog"
			content={({ update }) => (
				<Fragment>
					<PopupRepositionOnKeyChange repositionKey={repositionKey} update={update} />
					<div css={popupEnterAnimationStyle}>
						<Box xcss={popupStyles.contentWrapper} testId="remix-popup">
							<Box xcss={popupStyles.body}>
								<RemixOverlayContent
									onOptionSelect={onOptionSelect}
									options={options}
									subtitle={subtitle}
								/>
							</Box>
						</Box>
					</div>
				</Fragment>
			)}
			trigger={(triggerProps) => trigger(triggerProps)}
		/>
	);
}
